import { Injectable } from '@angular/core';

export interface Wisp {
  points: { x: number; y: number }[];
  alpha: number;
  width: number;
  speed: number;
  offset: number;
  hue: number;
  age: number;
  maxAge: number;
  fadingOut: boolean;
}

export type HexagonStateColor = 'default' | 'won' | 'lost';

@Injectable()
export class HexagonSmokeService {
  private wisps: Wisp[] = [];
  private ctx: CanvasRenderingContext2D | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private time = 0;
  private animationId: number | null = null;
  private stateColor: HexagonStateColor = 'default';

  private readonly CANVAS_WIDTH = 186;
  private readonly CANVAS_HEIGHT = 215;
  private readonly WISP_COUNT = 12;

  constructor() {}

  /**
   * Initialize the smoke animation on a canvas element
   */
  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.canvas.width = this.CANVAS_WIDTH;
    this.canvas.height = this.CANVAS_HEIGHT;
    this.ctx = this.canvas.getContext('2d');
    this.initWisps();
  }

  /**
   * Start the smoke animation loop
   */
  start(): void {
    if (this.animationId) return;

    const animate = () => {
      this.time += 16;
      this.render();
      this.animationId = requestAnimationFrame(animate);
    };
    this.animationId = requestAnimationFrame(animate);
  }

  /**
   * Stop the smoke animation
   */
  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Update the color state (affects smoke hue)
   */
  setStateColor(state: HexagonStateColor): void {
    this.stateColor = state;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();
    this.ctx = null;
    this.canvas = null;
    this.wisps = [];
  }

  private initWisps(): void {
    this.wisps = [];
    for (let i = 0; i < this.WISP_COUNT; i++) {
      this.wisps.push(this.createWisp());
    }
  }

  private createWisp(): Wisp {
    const centerX = this.CANVAS_WIDTH / 2;
    const centerY = this.CANVAS_HEIGHT / 2;

    const points: { x: number; y: number }[] = [];
    const numPoints = 10 + Math.floor(Math.random() * 6);
    const startAngle = Math.random() * Math.PI * 2;
    const curveDirection = Math.random() > 0.5 ? 1 : -1;

    for (let i = 0; i < numPoints; i++) {
      const t = i / (numPoints - 1);
      const wave = Math.sin(t * Math.PI * 2) * 0.3;
      const angle = startAngle + curveDirection * (t * Math.PI * 1.5 + wave);
      const radius = 35 + t * 55 + Math.sin(t * Math.PI) * 15;
      points.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    }

    return {
      points,
      alpha: 0.025 + Math.random() * 0.04,
      width: 12 + Math.random() * 25,
      speed: 0.0002 + Math.random() * 0.0004,
      offset: Math.random() * 1000,
      hue: 38 + Math.random() * 10,
      age: 0,
      maxAge: 8000 + Math.random() * 6000, // 8-14 seconds lifetime
      fadingOut: false,
    };
  }

  private getHue(): number {
    switch (this.stateColor) {
      case 'won':
        return 105 + Math.random() * 10; // Green
      case 'lost':
        return 0 + Math.random() * 10; // Red
      default:
        return 38 + Math.random() * 10; // Golden/orange
    }
  }

  private render(): void {
    if (!this.ctx || !this.canvas) return;

    const w = this.canvas.width;
    const h = this.canvas.height;
    const centerX = w / 2;
    const centerY = h / 2;

    // Clear with slight fade for trail effect
    this.ctx.fillStyle = 'rgba(22, 22, 22, 0.15)';
    this.ctx.fillRect(0, 0, w, h);

    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.wisps.forEach((wisp, index) => {
      // Update wisp age
      wisp.age += 16;

      // Calculate fade multiplier based on lifecycle
      let fadeMultiplier = 1;
      const fadeInDuration = 1500; // 1.5 seconds fade in
      const fadeOutStart = wisp.maxAge * 0.75; // Start fading at 75% of life

      if (wisp.age < fadeInDuration) {
        // Fade in
        fadeMultiplier = wisp.age / fadeInDuration;
      } else if (wisp.age > fadeOutStart) {
        // Fade out
        fadeMultiplier = 1 - (wisp.age - fadeOutStart) / (wisp.maxAge - fadeOutStart);
      }

      // Replace wisp when fully faded out
      if (wisp.age >= wisp.maxAge) {
        this.wisps[index] = this.createWisp();
        return;
      }

      const t = (this.time + wisp.offset) * wisp.speed;

      const animatedPoints = wisp.points.map((p, i) => {
        const phase = t + i * 0.3;
        const pointRatio = i / wisp.points.length;

        const wobbleX = Math.sin(phase * 1.5) * 12
          + Math.sin(phase * 2.7 + pointRatio * Math.PI) * 8
          + Math.sin(phase * 4.1) * 4;
        const wobbleY = Math.cos(phase * 1.8) * 12
          + Math.cos(phase * 3.2 + pointRatio * Math.PI) * 8
          + Math.cos(phase * 4.5) * 4;

        let x = p.x + wobbleX;
        let y = p.y + wobbleY;

        const dx = x - centerX;
        const dy = y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 40) {
          const pushOut = (40 - dist) / 40;
          x += (dx / dist) * pushOut * 25;
          y += (dy / dist) * pushOut * 25;
        }

        return { x, y };
      });

      this.ctx!.beginPath();

      if (animatedPoints.length > 2) {
        this.ctx!.moveTo(animatedPoints[0].x, animatedPoints[0].y);

        for (let i = 0; i < animatedPoints.length - 1; i++) {
          const p0 = animatedPoints[Math.max(0, i - 1)];
          const p1 = animatedPoints[i];
          const p2 = animatedPoints[i + 1];
          const p3 = animatedPoints[Math.min(animatedPoints.length - 1, i + 2)];

          const cp1x = p1.x + (p2.x - p0.x) / 6;
          const cp1y = p1.y + (p2.y - p0.y) / 6;
          const cp2x = p2.x - (p3.x - p1.x) / 6;
          const cp2y = p2.y - (p3.y - p1.y) / 6;

          this.ctx!.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
        }
      }

      const gradient = this.ctx!.createLinearGradient(
        animatedPoints[0].x, animatedPoints[0].y,
        animatedPoints[animatedPoints.length - 1].x, animatedPoints[animatedPoints.length - 1].y
      );

      const hue = this.getHue();
      const alpha = wisp.alpha * (0.7 + Math.sin(t * 2) * 0.3) * fadeMultiplier;
      gradient.addColorStop(0, `hsla(${hue}, 100%, 50%, 0)`);
      gradient.addColorStop(0.2, `hsla(${hue}, 100%, 50%, ${alpha * 0.5})`);
      gradient.addColorStop(0.5, `hsla(${hue}, 90%, 45%, ${alpha})`);
      gradient.addColorStop(0.8, `hsla(${hue}, 80%, 40%, ${alpha * 0.5})`);
      gradient.addColorStop(1, `hsla(${hue}, 70%, 35%, 0)`);

      this.ctx!.strokeStyle = gradient;
      this.ctx!.lineWidth = wisp.width * (0.6 + Math.sin(t * 1.5) * 0.4) * fadeMultiplier;
      this.ctx!.stroke();
    });
  }
}
